import { Button, Modal } from "react-bootstrap";

const PRExplainModal = ({
  show,
  onHide,
}: {
  show: boolean;
  onHide: () => void;
}) => (
  <Modal show={show} onHide={onHide} centered size="lg" scrollable>
    <Modal.Header closeButton>
      <Modal.Title>Guaranteed PR (GPR)</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <p>
        Computed using the EPC contract definition: the ratio of measured
        generation to the ideal generation the PV modules would produce
        under the observed plane-of-array irradiance, adjusted only for
        linear panel degradation.
      </p>
      <p>
        <strong>GPR = &Sigma; E_prod / &Sigma; E_pv</strong>
      </p>
      <p>
        with{" "}
        <strong>
          E_pv,j = P_nom &times; (H_j / G) &times; (1 &minus; k&middot;d)
        </strong>{" "}
        computed per settlement period:
      </p>
      <ul>
        <li>
          <strong>E_prod,j</strong> &mdash; metered generation for period j
          (processed readings).
        </li>
        <li>
          <strong>P_nom</strong> &mdash; installed capacity of the site in
          kWp, summed across energy matches.
        </li>
        <li>
          <strong>H_j</strong> &mdash; plane-of-array irradiance for period j
          from Meteonorm, per panel orientation, weighted by panel count.
        </li>
        <li>
          <strong>G</strong> &mdash; 1 kW/m&sup2; (standard test conditions).
        </li>
        <li>
          <strong>d</strong> &mdash; annual panel degradation rate derived
          from the site&apos;s panel warranty.
        </li>
        <li>
          <strong>k</strong> &mdash; year index from commissioning: 0
          throughout year 1, 1 throughout year 2, and so on. Applied per
          settlement period, so a window straddling a year boundary uses the
          correct k for each half.
        </li>
      </ul>
      <p>
        This denominator contains <strong>no operational loss factors</strong>.
        The gap between a healthy site&apos;s GPR and 100% is exactly the
        cumulative effect of the loss factors the EPC committed to. That gap
        is what the site&apos;s <strong>Designed PR</strong> represents
        (typically ~85%).
      </p>
      <p>
        The <strong>guaranteed threshold</strong> is configured per site as
        either a flat percentage, a fraction of the Designed PR, the Designed
        PR minus an absolute delta, or the max/min of two such terms. The
        platform evaluates the rule against the Designed PR to produce a
        single effective GPR threshold and classifies the site accordingly:
      </p>
      <ul>
        <li>
          <strong>Green</strong> when measured GPR &ge; threshold.
        </li>
        <li>
          <strong>Amber</strong> when measured GPR is within 5 percentage
          points below the threshold.
        </li>
        <li>
          <strong>Red</strong> when measured GPR is more than 5 percentage
          points below the threshold.
        </li>
      </ul>
      <p>
        <strong>Curtailment exclusion:</strong> Sites with a configured
        export limit are automatically checked for curtailment. A period is
        flagged as curtailed when metered export hits the limit (or, for
        zero-export sites, when import drops unusually low during
        generation). Flagged periods are excluded from both &Sigma; E_prod
        and &Sigma; E_pv, so curtailment does not artificially lower the
        GPR.
      </p>
      <p className="text-muted small mb-0">
        &ldquo;Last month&rdquo; covers the previous complete calendar
        month. &ldquo;Last 12 months&rdquo; covers the trailing 12 complete
        calendar months. Offtaker-only sites (no on-site PV) do not show a
        GPR.
      </p>
    </Modal.Body>
    <Modal.Footer>
      <Button variant="secondary" onClick={onHide}>
        Close
      </Button>
    </Modal.Footer>
  </Modal>
);

export default PRExplainModal;
